import React from "react";
import { ExternalLink, Github } from "lucide-react";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";

const CreativeProjects = ({ projects }) => {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 bg-gray-50 dark:bg-gray-900 transition-colors duration-500">
      <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white text-center mb-16 sm:mb-20">
        My Work
      </h1>
      <div className="flex justify-center mt-6 mb-10">
        <div className="w-24 h-[3px] bg-gradient-to-r from-gray-400 to-gray-600 dark:from-gray-600 dark:to-gray-300 rounded-full"></div>
      </div>
      {projects.map((project, i) => (
        <div
          key={i}
          className={`flex flex-col md:flex-row items-center gap-8 sm:gap-12 mb-16 sm:mb-20 group ${
            i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
          }`}
        >
          {/* Image / Mockup */}
          <div className="relative w-full md:w-1/2 rounded-3xl overflow-hidden shadow-2xl dark:shadow-black/40 transition-transform transform group-hover:scale-105">
            <img
              src={project.image}
              alt={project.title}
              className="w-full h-72 sm:h-96 md:h-[450px] object-cover"
            />

            {/* Tech badges */}
            {/* Tech badges (Desktop) */}
            <div className="absolute top-4 left-4 hidden md:flex flex-wrap gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {project.tech.map((t, idx) => (
                <span
                  key={idx}
                  className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-3 py-1 rounded-full text-xs font-semibold shadow-sm"
                >
                  {t}
                </span>
              ))}

              {/* Status indicator (Desktop with badges) */}
              {project.status && (
                <span
                  className="flex items-center gap-2 text-[10px] sm:text-xs font-semibold px-3 py-1 rounded-full
        bg-gray-200/30 dark:bg-gray-700/30 backdrop-blur-sm border border-gray-300/40 dark:border-gray-600/40
        text-white transition-all duration-300"
                >
                  <span
                    className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full flex-shrink-0 ${
                      project.status === "working"
                        ? "bg-green-400 animate-glow shadow-[0_0_6px_2px_rgba(72,187,120,0.6)]"
                        : "bg-red-500 animate-pulse shadow-[0_0_6px_2px_rgba(239,68,68,0.6)]"
                    }`}
                  ></span>
                  {project.status === "working" ? "Working" : "Building"}
                </span>
              )}
            </div>

            {/* Mobile-only Status indicator */}
            {project.status && (
              <div className="absolute top-4 left-4 md:hidden flex items-center gap-2  transition-opacity duration-300">
                <span
                  className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full flex-shrink-0 ${
                    project.status === "working"
                      ? "bg-green-400 animate-glow shadow-[0_0_6px_2px_rgba(72,187,120,0.6)]"
                      : "bg-red-500 animate-pulse shadow-[0_0_6px_2px_rgba(239,68,68,0.6)]"
                  }`}
                ></span>
                <span className="text-[10px] sm:text-xs font-semibold text-black">
                  {project.status === "working" ? "Working" : "Building"}
                </span>
              </div>
            )}

            {/* Desktop-only CTA buttons */}
            <div className="absolute bottom-4 right-4 hidden md:flex gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <Tippy content="Live Preview" theme="dark">
                <a
                  href={project.live}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-gray-800 dark:bg-gray-700 hover:bg-gray-700 dark:hover:bg-gray-600 text-white p-3 rounded-full shadow-lg dark:shadow-black/40 transition transform hover:scale-110"
                >
                  <ExternalLink size={18} />
                </a>
              </Tippy>
              <Tippy content="GitHub Code" theme="dark">
                <a
                  href={project.code}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-gray-900 dark:bg-gray-800 hover:bg-gray-800 dark:hover:bg-gray-700 text-white p-3 rounded-full shadow-lg dark:shadow-black/40 transition transform hover:scale-110"
                >
                  <Github size={18} />
                </a>
              </Tippy>
            </div>
          </div>

          {/* Project Details */}
          <div className="w-full md:w-1/2 flex flex-col gap-4 sm:gap-6 text-center md:text-left mt-6 md:mt-0">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              {project.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 text-base sm:text-lg">
              {project.desc}
            </p>

            {/* Process Steps */}
            <div className="flex flex-col gap-2 mt-4">
              <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
                🔹 <strong>Problem:</strong> {project.problem}
              </p>
              <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
                🔹 <strong>Solution:</strong> {project.solution}
              </p>
              <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
                🔹 <strong>Result:</strong> {project.result}
              </p>
            </div>

            {/* Mobile-only CTA buttons */}
            <div className="flex flex-col md:hidden gap-3 mt-6">
              <a
                href={project.live}
                target="_blank"
                rel="noreferrer"
                className="bg-gray-800 dark:bg-gray-700 hover:bg-gray-700 dark:hover:bg-gray-600 text-white p-3 rounded-lg shadow-md dark:shadow-black/40 text-center transition transform hover:scale-105"
              >
                Live Preview
              </a>
              <a
                href={project.code}
                target="_blank"
                rel="noreferrer"
                className="bg-gray-900 dark:bg-gray-800 hover:bg-gray-800 dark:hover:bg-gray-700 text-white p-3 rounded-lg shadow-md dark:shadow-black/40 text-center transition transform hover:scale-105"
              >
                GitHub Code
              </a>
            </div>
          </div>
        </div>
      ))}
    </section>
  );
};

export default CreativeProjects;
